import { Box, LinearProgress, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PlanLimit } from '@/graphql/generated/types.generated';
import { limitStatus } from '@/utils/limit-status';
import PlanLimitUpgrade, { ADDONS_PLAN_PATH } from '@/components/billing/PlanLimitUpgrade';
import { CardGiftcardRounded, ErrorOutlineRounded } from '@mui/icons-material';

export interface IntegrationQuotaView {
    used: number;
    /** `null` means the plan does not include the capability, which is NOT the same as 0 left. */
    limit?: number | null;
    remaining?: number | null;
    blocked: boolean;
    /** The limit is the free allowance, not something bought: shown with an "offer" caveat. */
    isFreeTier?: boolean;
}

interface IntegrationUsageMeterProps {
    quota?: IntegrationQuotaView | null;
    /** ISO date the quota rolls over. Billing-anchored per workspace, so never assume the 1st. */
    periodResetsAt?: string | null;
    loading: boolean;
    /** i18n key for the row label, e.g. the API or MCP wording. */
    labelKey: string;
    /** i18n key for the "not included in your plan" message. */
    unavailableKey: string;
    /**
     * i18n key explaining that the plan does not include this capability and the free
     * allowance is a temporary offer. Receives `{limit}`.
     */
    freeOfferKey: string;
    /** i18n key for the offer headline. Receives `{limit}`. */
    freeOfferTitleKey: string;
    /** i18n key for the headline once the quota is used up, i.e. requests are being rejected. */
    limitReachedTitleKey: string;
    /** i18n key for what happened, until when, and the fix. Receives `{limit}` and `{date}`. */
    limitReachedKey: string;
    /** Segments upsell conversion in PostHog. */
    planLimit: PlanLimit;
    dataId: string;
}

/**
 * Limit + used-so-far for one integration quota, mirroring the billing page's
 * SubscriptionLimits so the two read as the same system.
 *
 * A plan without the add-on is NOT the null case any more: it gets the free allowance
 * (`isFreeTier`), rendered as a normal meter plus an "offer, may change" note and the
 * self-serve upgrade. `limit: null` now only means a misconfigured row, and keeps the
 * plain upsell rather than a misleading 0 / 0 bar.
 */
export default function IntegrationUsageMeter({
    quota,
    periodResetsAt,
    loading,
    labelKey,
    unavailableKey,
    freeOfferKey,
    freeOfferTitleKey,
    limitReachedTitleKey,
    limitReachedKey,
    planLimit,
    dataId,
}: IntegrationUsageMeterProps) {
    const { t } = useTranslation();

    if (loading && !quota) {
        return (
            <Box display="flex" flexDirection="column" gap={0.5} data-id={`${dataId}-loading`}>
                <Skeleton variant="text" height={24} />
                <Skeleton variant="rectangular" height={4} />
                <Skeleton variant="text" width="40%" height={20} />
            </Box>
        );
    }

    if (!quota) return null;

    const limit = quota.limit ?? null;

    // No allowance at all: an upsell, not a meter.
    if (limit === null || limit <= 0) {
        return (
            <PlanLimitUpgrade
                planLimit={planLimit}
                message={t(unavailableKey)}
                variant="inline"
                sx={{ mt: 1 }}
            />
        );
    }

    const { nearLimit, full: overLimit } = limitStatus(quota.used, limit);
    // `blocked` is what the request guards enforce, so trust it too: the meter must never look
    // healthy while requests are being rejected.
    const full = overLimit || quota.blocked;
    const resetDate = periodResetsAt ? new Date(periodResetsAt).toLocaleDateString() : null;
    const remaining = Math.max(0, quota.remaining ?? limit - quota.used);
    const colorSx = full ? { color: 'error.dark' } : nearLimit ? { color: 'warning.dark' } : {};

    return (
        <Box display="flex" flexDirection="column" gap={0.5} data-id={dataId}>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" {...colorSx}>
                <Typography>{t(labelKey)}</Typography>
                <Typography data-id={`${dataId}-counter`}>
                    {t('billing.feature.limit.counter', {
                        current: quota.used.toLocaleString(),
                        limit: limit.toLocaleString(),
                    })}
                </Typography>
            </Box>

            <LinearProgress
                variant="determinate"
                value={Math.min(100, (quota.used * 100) / limit)}
                {...(nearLimit && { color: 'warning' as const })}
                {...(full && { color: 'error' as const })}
                data-id={`${dataId}-bar`}
            />

            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="body2" {...colorSx}>
                    {t('billing.feature.limit.support-text', { current: remaining.toLocaleString() })}
                </Typography>
                {resetDate && (
                    <Typography variant="body2" color="text.secondary" data-id={`${dataId}-reset`}>
                        {t('settings.workspace.api.usage.resets', { date: resetDate })}
                    </Typography>
                )}
            </Box>

            {full ? (
                // Requests are being rejected right now, so this outranks the free offer. It says
                // what broke and until when, with the way out as its one solid action: the free
                // tier adds the add-on, a paid plan raises its limit. The red lives in the meter
                // above; a second red block reads as an error screen, so the callout stays calm.
                <Box data-id={`${dataId}-limit-reached`} sx={{ mt: 1 }}>
                    <PlanLimitUpgrade
                        planLimit={planLimit}
                        variant="alert"
                        severity="info"
                        icon={<ErrorOutlineRounded fontSize="inherit" />}
                        title={t(limitReachedTitleKey)}
                        message={t(limitReachedKey, { limit: limit.toLocaleString(), date: resetDate ?? '' })}
                        ctaLabel={t(
                            quota.isFreeTier
                                ? 'settings.workspace.usage.free-offer.cta'
                                : 'settings.workspace.usage.limit-reached.cta',
                        )}
                        ctaVariant="contained"
                        path={ADDONS_PLAN_PATH}
                    />
                </Box>
            ) : (
                quota.isFreeTier && (
                    // Free allowance: an offer, not a plan entitlement, so it gets its own callout with
                    // the number up front and the self-serve upgrade as its single action.
                    <Box data-id={`${dataId}-free-offer`} sx={{ mt: 1 }}>
                        <PlanLimitUpgrade
                            planLimit={planLimit}
                            variant="alert"
                            severity="info"
                            icon={<CardGiftcardRounded fontSize="inherit" />}
                            title={t(freeOfferTitleKey, { limit: limit.toLocaleString() })}
                            message={t(freeOfferKey)}
                            ctaLabel={t('settings.workspace.usage.free-offer.cta')}
                            path={ADDONS_PLAN_PATH}
                        />
                    </Box>
                )
            )}
        </Box>
    );
}
